import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPut, getToken } from '../api';

function normalizePortfolioRow(row) {
  var r = row || {};
  return Object.assign({}, r, {
    monthKey: r.monthKey || r.MonthKey || '',
    login: r.login || r.Login || '',
    cu: r.cu || r.CU_Account || '',
    crmOpenDate: r.crmOpenDate || r.CRM_Account_Open_Date || '',
    accountType: r.accountType || r.Account_Type || '',
    isReactivated: r.isReactivated || r.IsReactivated || '',
    reactivationDate: r.reactivationDate || r.ReactivationDate || '',
  });
}

export function usePortfolio() {
  const opts = arguments.length > 0 && arguments[0] ? arguments[0] : {};
  const enabled = opts.enabled !== false;
  const initialDelayMs = typeof opts.initialDelayMs === 'number' ? opts.initialDelayMs : 0;
  const pageSize = typeof opts.pageSize === 'number' ? Math.max(1, Math.min(opts.pageSize, 2000)) : 2000;
  const loadAllPages = opts.loadAllPages !== false;
  const includeNotActiveAssignments = opts.includeNotActiveAssignments === true;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const loadPortfolio = useCallback(async (resetPage = true) => {
    if (!enabled) return;
    if (!getToken()) return;
    setLoading(true);
    setError(null);
    try {
      var extraQuery = includeNotActiveAssignments ? '&includeNotActiveAssignments=1' : '';
      if (loadAllPages) {
        var fullRes = await apiGet('/api/portfolio?all=1' + extraQuery);
        var allRows = fullRes.rows || [];
        setData(allRows.map(normalizePortfolioRow));
        setLastUpdate(fullRes.updatedAt || null);
        setCurrentPage(1);
        setTotal(fullRes.total || allRows.length);
        setHasMore(false);
      } else {
      var page = resetPage ? 1 : currentPage;
      var res = await apiGet('/api/portfolio?page=' + page + '&limit=' + pageSize + extraQuery);
      var rows = res.rows || [];
      setData(rows.map(normalizePortfolioRow));
      setLastUpdate(res.updatedAt || null);
      setCurrentPage(res.page || 1);
      setTotal(res.total || 0);
      setHasMore(res.hasMore || false);
      }
    } catch (err) {
      setError(err.message);
      console.error('Portfolio load error:', err);
      setData([]);
    }
    setLoading(false);
  }, [enabled, currentPage, pageSize, loadAllPages, includeNotActiveAssignments]);

  const loadMore = useCallback(async () => {
    if (loadAllPages) return;
    if (!enabled || !hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      var nextPage = currentPage + 1;
      var extraQuery = includeNotActiveAssignments ? '&includeNotActiveAssignments=1' : '';
      var res = await apiGet('/api/portfolio?page=' + nextPage + '&limit=' + pageSize + extraQuery);
      var newRows = (res.rows || []).map(normalizePortfolioRow);
      setData(function(prev) { return prev.concat(newRows); });
      setCurrentPage(nextPage);
      setTotal(res.total || 0);
      setHasMore(res.hasMore || false);
    } catch (err) {
      setError(err.message);
      console.error('Portfolio load more error:', err);
    }
    setLoadingMore(false);
  }, [enabled, hasMore, currentPage, loadingMore, pageSize, loadAllPages, includeNotActiveAssignments]);

  const uploadPortfolio = useCallback(async (parsedData) => {
    try {
      var res = await apiPut('/api/portfolio', { rows: parsedData });
      var now = res.updatedAt || null;
      setData((parsedData || []).map(normalizePortfolioRow));
      setLastUpdate(now);
      setTotal(parsedData ? parsedData.length : 0);
      setCurrentPage(1);
      setHasMore(false);
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Portfolio upload error:', err);
      return false;
    }
  }, []);

  useEffect(function() {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (!getToken()) {
      setLoading(false);
      return;
    }
    if (initialDelayMs > 0) {
      var timer = setTimeout(function() {
        loadPortfolio(true);
      }, initialDelayMs);
      return function() { clearTimeout(timer); };
    }
    loadPortfolio(true);
  }, [loadPortfolio, enabled, initialDelayMs]);

  return {
    portfolioData: data,
    portfolioLoading: loading,
    portfolioLoadingMore: loadingMore,
    portfolioLastUpdate: lastUpdate,
    portfolioError: error,
    portfolioPage: currentPage,
    portfolioTotal: total,
    portfolioHasMore: hasMore,
    uploadPortfolio,
    reloadPortfolio: function() { return loadPortfolio(true); },
    loadMorePortfolio: loadMore,
  };
}
